/**
 * Authentication and authorization error handling utilities.
 *
 * Use this module for:
 * - Classifying HTTP status codes into auth error types (classifyErrorStatus)
 * - Creating structured AuthError objects from responses (createAuthError)
 * - Checking if errors require re-authentication (isAuthError)
 * - Extracting error messages from API responses (extractErrorMessage)
 *
 * For general error message extraction from unknown error types,
 * use `utils/errorUtils.ts` instead.
 *
 * @module lib/apiErrorHandling
 */

export type AuthErrorType = 'unauthorized' | 'forbidden' | 'not_found' | 'server_error' | 'network_error' | 'unknown';

export interface AuthError {
  type: AuthErrorType;
  message: string;
  status: number;
  originalError?: Error;
}

/**
 * Extract error message from various response formats.
 * Handles both structured error responses and plain text.
 */
export function extractErrorMessage(
  data: any,
  raw: string = '',
  fallback: string = 'Request failed',
): string {
  if (!data) {
    if (raw && raw.trim()) {
      return raw.trim();
    }
    return fallback;
  }

  // Try common error message fields
  if (typeof data === 'object') {
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message.trim();
    }
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim();
    }
    if (typeof data.status === 'string' && data.status === 'error' && data.message) {
      return String(data.message).trim();
    }
  }

  // Fallback to string representation
  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }

  return fallback;
}

/**
 * Classify HTTP status codes into error types.
 */
export function classifyErrorStatus(status: number): AuthErrorType {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status >= 500) return 'server_error';
  if (status === 0) return 'network_error';
  return 'unknown';
}

/**
 * Create a standardized auth error from a response.
 */
export function createAuthError(
  response: Response,
  data: any = null,
  raw: string = '',
): AuthError {
  const status = response.status;
  const type = classifyErrorStatus(status);
  const message = extractErrorMessage(data, raw, getDefaultErrorMessage(type, status));

  return {
    type,
    message,
    status,
  };
}

/**
 * Get default error message based on error type.
 */
function getDefaultErrorMessage(type: AuthErrorType, status: number): string {
  switch (type) {
    case 'unauthorized':
      return 'Authentication required. Please sign in.';
    case 'forbidden':
      return 'Access denied. You do not have permission to perform this action.';
    case 'not_found':
      return 'Resource not found.';
    case 'server_error':
      return `Server error (${status}). Please try again later.`;
    case 'network_error':
      return 'Network error. Please check your connection.';
    default:
      return `Request failed (${status}).`;
  }
}

/**
 * Check if an error is an authentication/authorization error.
 */
export function isAuthError(error: AuthError): boolean {
  return error.type === 'unauthorized' || error.type === 'forbidden';
}

/**
 * Check if an error indicates admin access is required.
 */
export function isAdminRequiredError(error: AuthError): boolean {
  return error.type === 'forbidden' && (
    error.message.toLowerCase().includes('admin') ||
    error.message.toLowerCase().includes('permission')
  );
}

type AbortErrorLike = {
  name?: unknown;
  code?: unknown;
  message?: unknown;
};

function looksLikeErrorObject(value: unknown): value is AbortErrorLike {
  return typeof value === 'object' && value !== null;
}

export function isAbortError(error: unknown): boolean {
  if (!looksLikeErrorObject(error)) {
    return false;
  }

  if (error.name === 'AbortError') {
    return true;
  }

  // Some browsers surface aborts as DOMException.ABORT_ERR (code 20).
  if (error.code === 20) {
    return true;
  }

  if (typeof error.message === 'string') {
    const message = error.message.trim().toLowerCase();
    if (!message) {
      return false;
    }

    return (
      message === 'aborted' ||
      message.includes('aborterror') ||
      message.includes('signal is aborted') ||
      message.includes('operation was aborted') ||
      message.includes('request was aborted') ||
      message.includes('fetch was aborted')
    );
  }

  return false;
}
